import _ from "lodash";

/**
 * Populates the create_supportneed function declaration's enum values
 * from actual module data.
 *
 * @param {Array} tools - Deep-cloned tools array from geminiConfig
 * @param {Object} options
 * @param {Object} [options.botData] - Single bot data (for bot editor)
 * @param {Array}  [options.supportBots] - All support bots (for survey save)
 * @param {Array}  [options.selectedBotIds] - Selected bot IDs (for survey save)
 * @param {Object} [options.supportTopicCodes] - Topic code map { code: { title, ... } }
 * @returns {Array} tools with populated enums
 */
function populateCreateSupportneedEnums(tools, options = {}) {
    const { botData, supportBots, selectedBotIds, supportTopicCodes } = options;

    for (const toolGroup of tools) {
        if (!toolGroup.functionDeclarations) continue;

        for (const fn of toolGroup.functionDeclarations) {
            if (fn.name !== "create_supportneed") continue;

            const props = fn.parameters?.properties;
            if (!props) continue;

            // --- category enum + description ---
            // Only include topics from bots that have a support request target,
            // and exclude topics whose request type is "noRequests"
            const getTopicRequestType = (code, bot, topicSource) => {
                return bot?.config?.topicSettings?.[code]?.requesttype
                    || topicSource?.[code]?.requesttype
                    || 'requestWithConsent';
            };

            let topicCodes = [];
            if (botData?.config?.supportRequestTarget && botData?.config?.supportTopics) {
                const topicSource = botData.config?.topicData || supportTopicCodes || {};
                topicCodes = botData.config.supportTopics.filter(code =>
                    getTopicRequestType(code, botData, topicSource) !== 'noRequests'
                );
            } else if (supportBots && selectedBotIds) {
                const codeSet = new Set();
                selectedBotIds.forEach(id => {
                    const bot = supportBots.find(b => String(b.id) === String(id));
                    if (bot?.config?.supportRequestTarget && bot?.config?.supportTopics) {
                        const topicSource = bot.config?.topicData || supportTopicCodes || {};
                        bot.config.supportTopics.forEach(code => {
                            if (getTopicRequestType(code, bot, topicSource) !== 'noRequests') {
                                codeSet.add(code);
                            }
                        });
                    }
                });
                topicCodes = [...codeSet];
            }

            if (topicCodes.length > 0 && props.category) {
                props.category.enum = topicCodes;

                // Build "CODE (Title)" mappings and replace {topicMappings} variable
                const topicData = botData?.config?.topicData || {};
                const mappings = topicCodes.map(code => {
                    const title = topicData[code]?.title
                        || supportTopicCodes?.[code]?.title
                        || code;
                    return `${code} (${title})`;
                }).join(", ");

                if (props.category.description) {
                    props.category.description = props.category.description
                        .replace(/\{topicMappings}/g, mappings);
                }
            }

            // --- supporttype enum + description ---
            let supportTypes = [];
            if (botData?.config?.supportRequestTarget) {
                supportTypes = [botData.config.supportRequestTarget];
            } else if (supportBots && selectedBotIds) {
                const typeSet = new Set();
                selectedBotIds.forEach(id => {
                    const bot = supportBots.find(b => String(b.id) === String(id));
                    if (bot?.config?.supportRequestTarget) {
                        typeSet.add(bot.config.supportRequestTarget);
                    }
                });
                supportTypes = [...typeSet];
            }

            if (supportTypes.length > 0 && props.supporttype) {
                props.supporttype.enum = supportTypes;

                if (props.supporttype.description) {
                    props.supporttype.description = props.supporttype.description
                        .replace(/\{supportTypes}/g, supportTypes.join(", "));
                }
            }

            // --- followuptype enum + description ---
            if (props.followuptype) {
                const followupTypes = ["NOFOLLOWUP"];
                props.followuptype.enum = followupTypes;

                if (props.followuptype.description) {
                    props.followuptype.description = props.followuptype.description
                        .replace(/\{followupTypes}/g, followupTypes.join(", "));
                }
            }
        }
    }

    return tools;
}

/**
 * Builds the agentData object for saving to the aiagent table.
 *
 * @param {Object} geminiConfig - The config template from DB (model, tools, generationConfig)
 * @param {string} instructions - The system instruction text
 * @param {Array}  additionalTools - Extra tools (e.g., search_knowledge_base tool) to merge
 * @param {Object} options - { corpora, botData, supportBots, selectedBotIds, supportTopicCodes }
 * @returns {Object} Complete agentData object for POST to aiagent.php
 */
export function buildGeminiAgentData(geminiConfig, instructions, additionalTools = [], options = {}) {
    const { corpora, existingRagConfig, contents, selectedBotIds } = options;

    const model = geminiConfig?.model || "gemini-2.5-pro";
    const generationConfig = geminiConfig?.generationConfig
        ? _.cloneDeep(geminiConfig.generationConfig)
        : {};

    // Deep-clone and populate tools from config template
    let configTools = geminiConfig?.tools
        ? _.cloneDeep(geminiConfig.tools)
        : [];

    configTools = populateCreateSupportneedEnums(configTools, options);

    // Merge all functionDeclarations into a single tool object (Vertex AI requirement)
    const allDeclarations = [];
    [...configTools, ...additionalTools].forEach(tool => {
        if (tool.functionDeclarations) {
            allDeclarations.push(...tool.functionDeclarations);
        }
    });
    const mergedTools = allDeclarations.length > 0
        ? [{ functionDeclarations: allDeclarations }]
        : [];

    const toolConfig = geminiConfig?.toolConfig
        ? _.cloneDeep(geminiConfig.toolConfig)
        : undefined;

    const config = {
        geminiConfig: {
            systemInstruction: {
                parts: [{ text: instructions || "" }]
            },
            tools: mergedTools,
            generationConfig,
            ...(toolConfig && { toolConfig })
        }
    };

    // Add contents if provided (survey-level initial greeting)
    if (contents && contents.length > 0) {
        config.geminiConfig.contents = contents;
    }

    if (corpora && Object.keys(corpora).length > 0) {
        const ragDefaults = geminiConfig?.ragConfig || {};
        config.ragConfig = {
            topK: existingRagConfig?.topK ?? ragDefaults.topK ?? 10,
            vectorDistanceThreshold: existingRagConfig?.vectorDistanceThreshold ?? ragDefaults.vectorDistanceThreshold ?? null,
            corpora
        };
    }

    if (selectedBotIds && selectedBotIds.length > 0) {
        config.sourceModules = selectedBotIds;
    }

    const agentData = {
        model,
        config
    };

    return agentData;
}

/**
 * Builds the requestBody for generateContent calls (BotModuleTester).
 *
 * @param {Object} geminiConfig - The config template from DB
 * @param {string} instructions - The system instruction text
 * @param {Array}  contents - Gemini conversation contents array
 * @param {Array}  additionalTools - Extra tools to merge
 * @param {Object} options - { corpora, botData, supportBots, selectedBotIds, supportTopicCodes }
 * @returns {{ model: string, requestBody: Object }}
 */
export function buildGeminiRequestBody(geminiConfig, instructions, contents, additionalTools = [], options = {}) {
    const model = geminiConfig?.model || "gemini-2.5-pro";
    const generationConfig = geminiConfig?.generationConfig
        ? _.cloneDeep(geminiConfig.generationConfig)
        : {};

    let configTools = geminiConfig?.tools
        ? _.cloneDeep(geminiConfig.tools)
        : [];

    configTools = populateCreateSupportneedEnums(configTools, options);

    // Merge all functionDeclarations into a single tool object (Vertex AI requirement)
    const allDeclarations = [];
    [...configTools, ...additionalTools].forEach(tool => {
        if (tool.functionDeclarations) {
            allDeclarations.push(...tool.functionDeclarations);
        }
    });
    const mergedTools = allDeclarations.length > 0
        ? [{ functionDeclarations: allDeclarations }]
        : [];

    const toolConfig = geminiConfig?.toolConfig
        ? _.cloneDeep(geminiConfig.toolConfig)
        : undefined;

    const requestBody = {
        systemInstruction: {
            parts: [{ text: instructions || "" }]
        },
        contents,
        tools: mergedTools,
        generationConfig,
        ...(toolConfig && { toolConfig })
    };

    return { model, requestBody };
}
